import React, { useState, useEffect, useContext } from 'react';
import { Link } from 'react-router-dom';
import PropTypes from 'prop-types';
import { TabPane, Col, Row, Card, CardBody, Button, Label, Spinner } from 'reactstrap';
import { toast } from 'react-toastify';
import moment from 'moment';
import Switch from 'react-switch';

import BZAPIService from '../../../services/BZAPIService';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Select from 'react-select';
import Loader from '../../common/Loader';
import LoaderButton from '../../loader-button/LoaderButton';

import { VendorsContext } from '../../../context/VendorsContext';
import { DashboardContext } from '../../../context/DashboardContext';

// Import for donut chart
import * as echarts from 'echarts';
import ReactEcharts from 'echarts-for-react';
import AppContext from '../../../context/Context';
import { numberFormatter } from '../../../helpers/utils';
import { chartTheme, chartTooltip } from '../../../helpers/chartTheme';

import OverviewScheduleSummary from '../dynamic-form/OverviewScheduleSummary';
import { caughtOutcome, mapExecutionStatus, getExecutionText } from './executionStatusDisplay';

// Bug options for dropdown
const bugOptions = [
  { value: 'newBugsLastWeek', label: 'New bugs in last 7 days' },
  { value: 'updatedBugsLastWeek', label: 'Updated bugs in last 7 days' },
  { value: 'newBugsLastMonth', label: 'New bugs in last 30 days' },
  { value: 'updatedBugsLastMonth', label: 'Updated bugs in last 30 days' }
];

// Brick-style progress for Map / Parallel iteration counts. Always renders
// the same number of bricks regardless of iteration count — each brick
// represents `total / PROGRESS_BRICK_COUNT` iterations of work. So a 100-
// iteration Map fills 1 brick per 10 iterations, a 4-iteration Map fills
// 2.5 bricks per iteration. Keeps the visual width stable across runs.
// Bricks are painted left-to-right as completed (green) → failed (red) →
// running (yellow) → pending (gray). The leading-edge running brick (the
// first yellow one) opacity-pulses so the "current work" is unambiguous;
// other yellow bricks stay solid.
const PROGRESS_BRICK_COUNT = 10;

const ProgressBricks = ({ completed, failed, running, total }) => {
  if (!total || total <= 0) return null;

  const bricks = PROGRESS_BRICK_COUNT;
  const fraction = bricks / total;
  const completedBricks = Math.round(completed * fraction);
  const failedBricks = Math.round(failed * fraction);
  // Round-by-fraction can drift past the brick budget for small totals where
  // fraction > 1 (e.g. total=4 with completed=1, running=3 → completed=3,
  // running=8, sum=11). Clamp running to whatever's left in the budget so the
  // total is always exactly PROGRESS_BRICK_COUNT — running absorbs any
  // rounding drift since it's the most "approximate" category.
  const runningBricks = Math.max(0, Math.min(Math.round(running * fraction), bricks - completedBricks - failedBricks));
  const pendingBricks = Math.max(0, bricks - completedBricks - failedBricks - runningBricks);

  // Palette: green for completed, bugzero navy for not-yet-completed (failed
  // + running alike). Every running brick opacity-pulses in unison so the
  // 'active work' signal scales with how many iterations are actually in
  // flight (a Map with MaxConcurrency=3 will pulse 3 bricks; with =1, just
  // one). Failed bricks are the same navy but solid — distinguished by NOT
  // pulsing. Pending bricks stay outlined gray (read as 'empty').
  const cells = [];
  for (let i = 0; i < completedBricks; i++) {
    cells.push({ key: `c${i}`, cls: 'completed', color: '#28a745', pulse: false });
  }
  for (let i = 0; i < failedBricks; i++) {
    cells.push({ key: `f${i}`, cls: 'failed', color: 'var(--brand-red)', pulse: false });
  }
  for (let i = 0; i < runningBricks; i++) {
    cells.push({ key: `r${i}`, cls: 'running', color: 'var(--navy-line)', pulse: true });
  }
  for (let i = 0; i < pendingBricks; i++) {
    cells.push({ key: `p${i}`, cls: 'pending', color: 'var(--line-soft)', pulse: false });
  }

  // Each brick is flex: 1 (stretches to fill the container) but capped at
  // 18px so on wide cards the bricks stay block-shaped instead of becoming
  // banner-thin. On narrow cards they shrink. 10 bricks * 18px max + 9 *
  // 3px gap = ~207px max — fits comfortably in any reasonable card width.
  // Pending bricks get a full inset border (via box-shadow so the size
  // stays identical to colored bricks) so they're visible against a white
  // card background.
  return (
    <div style={{ display: 'flex', gap: '3px', maxWidth: '210px' }}>
      {cells.map(c => (
        <div
          key={c.key}
          style={{
            flex: 1,
            maxWidth: '18px',
            height: '16px',
            background: c.color,
            borderRadius: '2px',
            boxShadow: c.cls === 'pending' ? 'inset 0 0 0 1px var(--line-strong)' : 'inset 0 -1px 0 rgba(0,0,0,0.12)',
            ...(c.pulse ? { animation: 'bz-brick-pulse 1.2s ease-in-out infinite' } : {})
          }}
        />
      ))}
    </div>
  );
};

ProgressBricks.propTypes = {
  completed: PropTypes.number.isRequired,
  failed: PropTypes.number.isRequired,
  running: PropTypes.number.isRequired,
  total: PropTypes.number.isRequired
};

// Severity color mapping
const getSeverityColor = priority => {
  const key = (priority || '').toLowerCase();
  if (['blocker', 'severe', 'critical', 'high', 'catastrophic'].some(k => key.includes(k))) return '#c9302c';
  if (['major', 'medium', 'moderate'].some(k => key.includes(k))) return '#f0ad4e';
  if (['minor', 'low', 'informational'].some(k => key.includes(k))) return '#5cb85c';
  if (key.includes('unspecified')) return '#90919a';
  return '#f0ad4e';
};

export default function TabOne({
  vendorId,
  vendorLabel,
  activeTab,
  setActiveTab,
  snIntegrationCheck,
  isConfigured,
  modalOpen,
  setModalOpen,
  setIsForceRun,
  timeUnit,
  setTimeUnit,
  timeValue,
  setTimeValue,
  schedulingMode: propSchedulingMode,
  setSchedulingMode: propSetSchedulingMode,
  schedules: propSchedules,
  setSchedules: propSetSchedules,
  loading,
  isServiceActive,
  setIsServiceActive,
  execStatus,
  execStatusLoading,
  refreshExecStatus,
  onEditSchedule
}) {
  // Determine if this is wizard mode (read-only overview) or legacy mode (full scheduling editor)
  const isWizardMode = !propSetSchedulingMode;

  const [priorityStats, setPriorityStats] = useState([]);
  const [isLoading, setLoading] = useState(true);
  const [vendorApiStatus, setVendorApiStatus] = useState();
  const [lastChecked, setLastChecked] = useState(null);
  const [selectedBugOption, setSelectedBugOption] = useState(bugOptions[0]);
  const [allBugData, setAllBugData] = useState({});
  const [isLoadingService, setIsLoadingService] = useState(false);
  const [serviceData, setServiceData] = useState(null);

  // Enhanced scheduling state (only used in legacy mode)
  const [localSchedulingMode, setLocalSchedulingMode] = useState('interval');
  const [localSchedules, setLocalSchedules] = useState([]);

  const schedulingMode = propSchedulingMode !== undefined ? propSchedulingMode : localSchedulingMode;
  const setSchedulingMode = propSetSchedulingMode || setLocalSchedulingMode;
  const schedules = propSchedules !== undefined ? propSchedules : localSchedules;
  const setSchedules = propSetSchedules || setLocalSchedules;

  // eslint-disable-next-line
  const [vendors, setVendors] = useContext(VendorsContext);
  // eslint-disable-next-line
  const [dashboard, setDashboard] = useContext(DashboardContext);
  const { isDark } = useContext(AppContext);

  // Find current vendor
  const currentVendor = vendors?.find(v => v.id === vendorId);

  const managedProductCount =
    dashboard?.managedProductsCount?.find(p => p.vendorId === vendorId)?.managedProductCount ?? 0;
  const isCloudVendor = currentVendor?.cloudVendor === 1;

  const selectStyles = {
    option: (styles, { isFocused, isSelected }) => ({
      ...styles,
      color: isFocused || isSelected ? 'white' : 'var(--ink)'
    })
  };

  const timeUnitOptions = [
    { label: 'Minutes', value: 'minute' },
    { label: 'Hours', value: 'hour' }
  ];

  const mins = [
    { label: '15', value: '15' },
    { label: '30', value: '30' },
    { label: '45', value: '45' },
    { label: '60', value: '60' }
  ];

  const hrs = Array.from({ length: 24 }, (_, i) => ({ label: String(i + 1), value: i + 1 }));

  const daysOfWeek = [
    { label: 'Monday', value: 'MON' },
    { label: 'Tuesday', value: 'TUE' },
    { label: 'Wednesday', value: 'WED' },
    { label: 'Thursday', value: 'THU' },
    { label: 'Friday', value: 'FRI' },
    { label: 'Saturday', value: 'SAT' },
    { label: 'Sunday', value: 'SUN' }
  ];

  // Get integration guide URL
  const getIntegrationGuideUrl = () => {
    const guides = {
      postgre: '/documentation/vendor-configuration-guides/',
      msftaccess: '/documentation/vendor-configuration-guides/',
      msftsql: '/documentation/vendor-configuration-guides/',
      msftserver: '/documentation/vendor-configuration-guides/microsoft-integration-guide',
      msft: '/documentation/vendor-configuration-guides/microsoft-integration-guide',
      msft365: '/documentation/vendor-configuration-guides/microsoft365-integration-guide',
      rh: '/documentation/vendor-configuration-guides/red-hat-integration-guide'
    };
    return guides[vendorId] || `/documentation/vendor-configuration-guides/${vendorId}-integration-guide`;
  };

  async function getScheduleSettings() {
    try {
      const res = await BZAPIService.getBugServiceSchedule(vendorId);
      const data = res?.data;

      const defaultUnit = isCloudVendor ? 'minute' : 'hour';
      const defaultValue = isCloudVendor ? mins[1] : hrs[3];

      if (data?.mode && data.rules && data.rules.length > 0) {
        const rule = data.rules[0];
        setSchedulingMode(data.mode);

        if (data.mode === 'interval') {
          let unit, number;

          if (rule.awsCron) {
            const cronParts = rule.awsCron
              .replace('cron(', '')
              .replace(')', '')
              .split(' ');
            const minutePart = cronParts[0];
            const hourPart = cronParts[1];

            if (hourPart.includes('*/')) {
              unit = 'hour';
              number = parseInt(hourPart.split('*/')[1]);
            } else if (hourPart === '*' && !minutePart.includes('*') && !minutePart.includes(',')) {
              unit = 'hour';
              number = 1;
            } else if (minutePart.includes('*/')) {
              unit = 'minute';
              number = parseInt(minutePart.split('*/')[1]);
            }
          }

          if (!unit && rule.schedule) {
            unit = rule.schedule.timeUnit || rule.schedule.unit;
            number = rule.schedule.number || rule.schedule.every;
            if (unit === 'hours') unit = 'hour';
            if (unit === 'minutes') unit = 'minute';
          }

          if (!unit && data.timeUnit) {
            unit = data.timeUnit;
            number = data.number;
          }

          if (!unit) unit = defaultUnit;

          setTimeUnit(unit);
          setTimeValue(number ? { label: number, value: number } : defaultValue);
        } else if (data.mode === 'calendar') {
          const parsedSchedules = data.rules.map(rule => {
            const { awsCron, nextRunUTC, name } = rule;
            const days = extractDaysFromCron(awsCron);
            const times = extractTimesFromCron(awsCron);
            return { days, times, nextRunUTC, name, awsCron };
          });
          setSchedules(parsedSchedules);
        }
      } else if (data?.timeUnit || data?.number) {
        setSchedulingMode('interval');
        setTimeUnit(data.timeUnit || defaultUnit);
        const val = data.number;
        setTimeValue(val ? { label: val, value: val } : defaultValue);
      } else {
        setSchedulingMode('interval');
        setTimeUnit(defaultUnit);
        setTimeValue(defaultValue);
      }
    } catch (e) {
      console.log(e);
      const defaultUnit = isCloudVendor ? 'minute' : 'hour';
      const defaultValue = isCloudVendor ? mins[1] : hrs[3];
      setTimeValue(defaultValue);
      setTimeUnit(defaultUnit);
      setSchedulingMode('interval');
    }
  }

  function extractDaysFromCron(awsCron) {
    const parts = awsCron
      .replace('cron(', '')
      .replace(')', '')
      .split(' ');
    if (parts.length >= 5) {
      const dayPart = parts[4];
      if (dayPart === '*') return ['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN'];
      if (dayPart === '?') return [];
      return dayPart.split(',').map(day => {
        if (day.length === 3) return day.toUpperCase();
        const dayMap = { '1': 'SUN', '2': 'MON', '3': 'TUE', '4': 'WED', '5': 'THU', '6': 'FRI', '7': 'SAT' };
        return dayMap[day] || day.toUpperCase();
      });
    }
    return [];
  }

  function extractTimesFromCron(awsCron) {
    const parts = awsCron
      .replace('cron(', '')
      .replace(')', '')
      .split(' ');
    if (parts.length >= 2) {
      const minutePart = parts[0];
      const hourPart = parts[1];

      if (hourPart.includes('*/')) {
        const interval = parseInt(hourPart.split('*/')[1]);
        const times = [];
        for (let h = 0; h < 24; h += interval) {
          const m = minutePart === '*' ? 0 : parseInt(minutePart);
          times.push(`${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`);
        }
        return times;
      }

      if (hourPart.includes(',')) {
        return hourPart.split(',').map(hour => {
          const h = parseInt(hour);
          const m = minutePart === '*' ? 0 : parseInt(minutePart);
          return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`;
        });
      }

      if (hourPart !== '*' && !hourPart.includes('/')) {
        const h = parseInt(hourPart);
        const m = minutePart === '*' ? 0 : parseInt(minutePart);
        return [`${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`];
      }
    }
    return ['06:00'];
  }

  function validateSchedulingConfiguration() {
    if (schedulingMode === 'interval') {
      return timeUnit && timeValue && timeValue.value;
    } else if (schedulingMode === 'calendar') {
      if (schedules.length === 0) return false;
      return schedules.every(schedule => {
        const hasDays = schedule.days && schedule.days.length > 0;
        const hasTimes = schedule.times && schedule.times.length > 0;
        const validTimes = schedule.times.every(time => /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/.test(time));
        return hasDays && hasTimes && validTimes;
      });
    }
    return false;
  }

  async function setBugServiceFrequency() {
    let data;
    if (schedulingMode === 'interval') {
      data = { mode: 'interval', timeUnit, number: parseInt(timeValue.value) };
    } else if (schedulingMode === 'calendar') {
      data = {
        mode: 'calendar',
        schedules: schedules.map(schedule => ({ days: schedule.days, times: schedule.times }))
      };
    }

    if (vendorId.length) {
      try {
        await BZAPIService.postBugServiceSchedule(vendorId, data, { method: 'POST' });
        await getScheduleSettings();
      } catch (e) {
        console.log(e);
        throw e;
      }
    }
  }

  async function saveScheduleOnly() {
    if (!validateSchedulingConfiguration()) {
      toast.error('Please complete the scheduling configuration before saving.');
      return;
    }
    try {
      await setBugServiceFrequency();
      toast.success('Schedule saved successfully!');
    } catch (error) {
      console.error('Error saving schedule:', error);
      toast.error('Failed to save schedule. Please try again.');
    }
  }

  async function forceRun() {
    if (!validateSchedulingConfiguration()) {
      toast.error('Please complete the scheduling configuration before saving.');
      return;
    }
    try {
      await setBugServiceFrequency();
      toast.success('Schedule saved successfully!');
      setModalOpen(!modalOpen);
      setIsForceRun(true);
      // The actual state-machine trigger fires inside handleSave (after the
      // user confirms in the modal); handleSave is responsible for poking
      // refreshExecStatus so we don't double-fetch here.
    } catch (error) {
      console.error('Error saving schedule:', error);
      toast.error('Failed to save schedule. Please try again.');
    }
  }

  function addNewSchedule() {
    setSchedules([...schedules, { days: [], times: ['06:00'] }]);
  }

  function updateSchedule(index, field, value) {
    const updatedSchedules = [...schedules];
    updatedSchedules[index] = { ...updatedSchedules[index], [field]: value };
    setSchedules(updatedSchedules);
  }

  function removeSchedule(index) {
    setSchedules(schedules.filter((_, i) => i !== index));
  }

  function addTimeToSchedule(scheduleIndex) {
    const updatedSchedules = [...schedules];
    updatedSchedules[scheduleIndex].times.push('06:00');
    setSchedules(updatedSchedules);
  }

  function updateTimeInSchedule(scheduleIndex, timeIndex, newTime) {
    const updatedSchedules = [...schedules];
    updatedSchedules[scheduleIndex].times[timeIndex] = newTime;
    setSchedules(updatedSchedules);
  }

  function removeTimeFromSchedule(scheduleIndex, timeIndex) {
    const updatedSchedules = [...schedules];
    if (updatedSchedules[scheduleIndex].times.length > 1) {
      updatedSchedules[scheduleIndex].times.splice(timeIndex, 1);
      setSchedules(updatedSchedules);
    }
  }

  function buildVendorStat(dataSet, colors, activeVendors) {
    const vendorPriorityStats = {};
    activeVendors?.forEach(vendor => {
      vendorPriorityStats[vendor.id] = [];
    });

    dataSet?.forEach((bugPriority, id) => {
      if (!vendorPriorityStats[bugPriority.vendorId]) vendorPriorityStats[bugPriority.vendorId] = [];
      const colorIndex = vendorPriorityStats[bugPriority.vendorId].length;
      vendorPriorityStats[bugPriority.vendorId].push({
        id,
        value: bugPriority.priorityCount,
        name: bugPriority.priority,
        color: bugPriority.priority === 'Unspecified' ? '#90919a' : colors[colorIndex]
      });
    });
    return vendorPriorityStats;
  }

  async function updateServices() {
    try {
      setLoading(true);
      const dash = await BZAPIService.getDashboard();
      setDashboard(dash);

      const colors = [
        '#ff475f',
        '#000000',
        '#961ca2',
        '#35bc8e',
        '#90919a',
        '#c1bdb7',
        '#B54E34',
        '#253544',
        '#48384B',
        '#816577',
        '#445A57',
        '#617E71',
        '#829A92',
        '#567387',
        '#8096A8',
        '#816477'
      ];

      // Build stats for all time ranges
      const newBugsLastWeek = buildVendorStat(dash?.newBugsLastWeek, colors, vendors);
      const updatedBugsLastWeek = buildVendorStat(dash?.updatedBugsLastWeek, colors, vendors);
      const newBugsLastMonth = buildVendorStat(dash?.newBugsLastMonth, colors, vendors);
      const updatedBugsLastMonth = buildVendorStat(dash?.updatedBugsLastMonth, colors, vendors);

      setAllBugData({
        newBugsLastWeek,
        updatedBugsLastWeek,
        newBugsLastMonth,
        updatedBugsLastMonth
      });

      // Set initial stats
      setPriorityStats(newBugsLastWeek[vendorId] || []);

      // Find service data for this vendor
      const service = dash?.services?.find(s => s?.id === vendorId + '-bs');
      setServiceData(service);

      setLoading(false);
    } catch (e) {
      console.log(e);
      setLoading(false);
    }
  }

  async function getVendorHealthStatus() {
    try {
      const res = await BZAPIService.getVendorHealthStatus(vendorId);
      const vendorData = res?.vendors?.[0] || null;
      setVendorApiStatus(vendorData);
      setLastChecked(res?.lastUpdated || vendorData?.currentStatus?.timestamp || null);
    } catch (e) {
      console.log('Error fetching vendor health status:', e);
      setVendorApiStatus(null);
      setLastChecked(null);
    }
  }

  async function setService(e) {
    setIsLoadingService(true);
    try {
      const res = await BZAPIService.setStatus(vendorId, e === true ? 1 : 0);
      setIsServiceActive(!!res?.data?.concurrency);
      toast.success(`Bug service has been ${e ? 'enabled' : 'disabled'}`);
      setIsLoadingService(false);
      refreshExecStatus();
    } catch (err) {
      console.log(err);
      setIsLoadingService(false);
      toast.error(`Failed to ${e ? 'enable' : 'disable'} service`);
    }
  }

  function handleBugOptionChange(option) {
    setSelectedBugOption(option);
    setPriorityStats(allBugData[option.value]?.[vendorId] || []);
  }

  useEffect(() => {
    setLoading(true);
    if (vendors?.length) {
      getVendorHealthStatus();
      updateServices();
    }
    // eslint-disable-next-line
  }, [vendorId, activeTab, vendors]);

  useEffect(() => {
    if (!isWizardMode) {
      getScheduleSettings();
    }
    // eslint-disable-next-line
  }, [vendorId, vendors]);

  // Donut chart options
  const getChartOption = () => {
    const t = chartTheme(isDark);
    const chartData = priorityStats.map(item => ({
      ...item,
      itemStyle: { color: getSeverityColor(item.name) }
    }));

    return {
      tooltip: chartTooltip(t, {
        trigger: 'item',
        padding: [7, 10],
        formatter: params => `<strong>${params.data.name}:</strong> ${params.value}`
      }),
      legend: { show: false },
      series: [
        {
          type: 'pie',
          radius: ['60%', '85%'],
          center: ['50%', '50%'],
          avoidLabelOverlap: false,
          label: { show: false },
          labelLine: { show: false },
          itemStyle: {
            borderWidth: 2,
            borderColor: t.bg
          },
          data: chartData.length > 0 ? chartData : [{ value: 1, name: 'No bugs', itemStyle: { color: t.empty } }]
        }
      ]
    };
  };

  const totalBugs = priorityStats.reduce((sum, item) => sum + (item.value || 0), 0);

  // Get status color and icon
  const getStatusColor = status => {
    switch (status) {
      case 'OPERATIONAL':
        return 'green';
      case 'NOT CONFIGURED':
      case 'PENDING':
        return 'blue';
      case 'ERROR':
        return 'red';
      case 'RUNNING':
        return 'light-blue';
      default:
        return 'warning';
    }
  };

  const getStatusIcon = status => {
    switch (status) {
      case 'OPERATIONAL':
        return 'check';
      case 'RUNNING':
        return 'clock';
      default:
        return 'exclamation';
    }
  };

  const execBadgeStatus = mapExecutionStatus(execStatus);
  const statusColor = getStatusColor(execBadgeStatus);
  const statusIcon = getStatusIcon(execBadgeStatus);
  const execText = getExecutionText(execStatus, execStatusLoading);
  // The caught error's own message under the run line: red for a failure, muted
  // for a vendor that just isn't configured yet.
  const execFailure = caughtOutcome(execStatus, 'FAILED');
  const execNote = (execFailure || caughtOutcome(execStatus, 'NOT_CONFIGURED'))?.message;
  const execNoteClass = execFailure ? 'text-danger' : 'text-muted';

  return (
    <div style={{ minWidth: '100%' }} className="tab-first-card-flush">
      <TabPane tabId="1">
        {activeTab === '1' && (
          <>
            {loading ? (
              <Loader text="Loading overview..." />
            ) : !snIntegrationCheck && !loading ? (
              <Card className="mb-4" style={{ borderTopLeftRadius: 0 }}>
                <CardBody>
                  <p className="text-center">Please setup your ServiceNow integration.</p>
                  <div className="text-center">
                    <Link className="bz-btn bz-btn--primary" to="/integrations/servicenow">
                      Setup now
                    </Link>
                  </div>
                </CardBody>
              </Card>
            ) : (
              <>
                {/* Section 1: Bug Service Status */}
                <Card style={{ marginBottom: '5px', borderTopLeftRadius: 0 }}>
                  <CardBody>
                    <Row className="align-items-center">
                      <Col>
                        <div className="d-flex align-items-center mb-2">
                          {execStatus?.hasExecution && execStatus.isRunning && (
                            <span
                              aria-hidden="true"
                              className="d-inline-block me-2"
                              style={{
                                width: 10,
                                height: 10,
                                borderRadius: '50%',
                                backgroundColor: 'rgb(194, 71, 41)',
                                animation: 'bz-pulse 1.5s ease-in-out infinite'
                              }}
                            />
                          )}
                          <h6 className="mb-0 me-3" style={{ fontWeight: 600 }}>
                            {currentVendor?.name?.replace('Systems', 'Networks')} bug service
                          </h6>
                          <div className="d-flex align-items-center">
                            {isLoadingService && <Spinner size="sm" className="me-2" />}
                            <Switch
                              onChange={e => setService(e)}
                              checked={!!isServiceActive}
                              onColor="#253746"
                              uncheckedIcon={false}
                              checkedIcon={false}
                              height={20}
                              width={40}
                              handleDiameter={16}
                              disabled={!isConfigured}
                            />
                            <Label className="mb-0 ms-2 small text-muted">Disable / Enable</Label>
                          </div>
                        </div>
                        <p className="mb-0" style={{ fontSize: '0.95rem', color: 'var(--ink-soft)' }}>
                          {execText}
                        </p>
                        {execNote && (
                          <p className={`mb-0 mt-1 small ${execNoteClass}`} style={{ whiteSpace: 'pre-wrap' }}>
                            {execNote}
                          </p>
                        )}
                        {execStatus?.isRunning && execStatus.progress && execStatus.progress.total > 0 && (
                          <div className="mt-2">
                            <ProgressBricks
                              completed={execStatus.progress.completed}
                              failed={execStatus.progress.failed}
                              running={execStatus.progress.running}
                              total={execStatus.progress.total}
                            />
                            <div className="small text-muted mt-1">
                              {Math.min(
                                100,
                                Math.round((execStatus.progress.completed / execStatus.progress.total) * 100)
                              )}
                              % complete
                              {execStatus.progress.failed > 0 && ` · ${execStatus.progress.failed} failed`}
                            </div>
                          </div>
                        )}
                        {execStatus?.hasExecution && execStatus.isRunning && execStatus.startedAt && (
                          <div className="small text-muted mt-2">
                            <span>Started {moment(execStatus.startedAt).fromNow()}</span>
                          </div>
                        )}
                      </Col>
                      <Col xs="auto" className="text-end">
                        <div className="d-flex align-items-center">
                          <span className="fa-stack fa-xs me-2">
                            <FontAwesomeIcon icon="circle" className={`branding-${statusColor} fa-stack-2x`} />
                            <FontAwesomeIcon icon={statusIcon} className="fa-stack-1x" inverse />
                          </span>
                          <span className={`branding-${statusColor} fw-semibold`} style={{ fontSize: '0.875rem' }}>
                            {execBadgeStatus}
                          </span>
                        </div>
                      </Col>
                    </Row>
                    {/* Animation keyframes — colocated with the card so reverting removes the rules too. */}
                    <style>{`
                      @keyframes bz-pulse {
                        0%, 100% { opacity: 1; transform: scale(1); }
                        50% { opacity: 0.45; transform: scale(1.2); }
                      }
                      @keyframes bz-brick-pulse {
                        0%, 100% { opacity: 1; }
                        50% { opacity: 0.5; }
                      }
                    `}</style>
                  </CardBody>
                </Card>

                {/* Section 2: Configure run frequency + Vendor API Status */}
                <div className="d-flex" style={{ marginBottom: '5px', gap: '5px' }}>
                  {/* Left Card: Schedule - wizard mode shows read-only summary, legacy mode shows full editor */}
                  <div style={{ flex: 1 }}>
                    {isWizardMode ? (
                      <OverviewScheduleSummary
                        schedulingMode={schedulingMode}
                        timeUnit={timeUnit}
                        timeValue={timeValue}
                        schedules={schedules}
                        onEditClick={onEditSchedule || (() => setActiveTab('2'))}
                      />
                    ) : (
                      <Card className="h-100">
                        <CardBody>
                          <h6 className="mb-3" style={{ fontWeight: 600 }}>
                            Configure run frequency
                          </h6>

                          {/* Scheduling Mode Radio Buttons */}
                          <div className="mb-3 d-flex align-items-center" style={{ gap: '20px' }}>
                            <label
                              className="d-flex align-items-center mb-0"
                              style={{ cursor: 'pointer' }}
                              onClick={() => setSchedulingMode('interval')}
                            >
                              <span
                                style={{
                                  width: '18px',
                                  height: '18px',
                                  borderRadius: '50%',
                                  border: '2px solid #253746',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  marginRight: '8px'
                                }}
                              >
                                {schedulingMode === 'interval' && (
                                  <span
                                    style={{
                                      width: '10px',
                                      height: '10px',
                                      borderRadius: '50%',
                                      backgroundColor: '#253746'
                                    }}
                                  />
                                )}
                              </span>
                              <span>Interval</span>
                            </label>
                            <label
                              className="d-flex align-items-center mb-0"
                              style={{ cursor: 'pointer' }}
                              onClick={() => setSchedulingMode('calendar')}
                            >
                              <span
                                style={{
                                  width: '18px',
                                  height: '18px',
                                  borderRadius: '50%',
                                  border: '2px solid #253746',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  marginRight: '8px'
                                }}
                              >
                                {schedulingMode === 'calendar' && (
                                  <span
                                    style={{
                                      width: '10px',
                                      height: '10px',
                                      borderRadius: '50%',
                                      backgroundColor: '#253746'
                                    }}
                                  />
                                )}
                              </span>
                              <span>Calendar</span>
                            </label>
                          </div>

                          {/* Interval Mode UI */}
                          {schedulingMode === 'interval' && (
                            <Row className="mb-3">
                              <Col xs={6}>
                                <Select
                                  options={timeUnitOptions}
                                  value={timeUnitOptions.find(el => el.value === timeUnit)}
                                  onChange={e => {
                                    if (e.value === 'minute') setTimeValue(mins[1]);
                                    else setTimeValue(hrs[3]);
                                    setTimeUnit(e.value);
                                  }}
                                  menuPortalTarget={document.body}
                                  styles={selectStyles}
                                  theme={theme => ({
                                    ...theme,
                                    borderRadius: 5,
                                    colors: { ...theme.colors, primary25: '#253746', primary: '#bd472a' }
                                  })}
                                />
                              </Col>
                              <Col xs={6}>
                                <Select
                                  options={timeUnit === 'minute' ? mins : hrs}
                                  value={timeValue}
                                  placeholder="Select frequency"
                                  onChange={e => setTimeValue(e)}
                                  menuPortalTarget={document.body}
                                  styles={selectStyles}
                                  theme={theme => ({
                                    ...theme,
                                    borderRadius: 5,
                                    colors: { ...theme.colors, primary25: '#253746', primary: '#bd472a' }
                                  })}
                                />
                              </Col>
                            </Row>
                          )}

                          {/* Calendar Mode UI */}
                          {schedulingMode === 'calendar' && (
                            <div className="mb-3">
                              {schedules.length === 0 && (
                                <Button color="falcon-default" size="sm" onClick={addNewSchedule}>
                                  <FontAwesomeIcon icon="plus" className="me-1" /> Add Schedule
                                </Button>
                              )}

                              {schedules.map((schedule, scheduleIndex) => (
                                <div key={scheduleIndex} className="mb-3 p-2 border rounded">
                                  <div className="d-flex justify-content-between align-items-center mb-2">
                                    <small className="fw-bold">Schedule {scheduleIndex + 1}</small>
                                    {schedules.length > 1 && (
                                      <Button
                                        color="falcon-danger"
                                        size="sm"
                                        onClick={() => removeSchedule(scheduleIndex)}
                                      >
                                        <FontAwesomeIcon icon="trash" />
                                      </Button>
                                    )}
                                  </div>
                                  <div className="mb-2">
                                    <small className="text-muted">Days:</small>
                                    <Select
                                      isMulti
                                      options={daysOfWeek}
                                      value={daysOfWeek.filter(day => schedule.days.includes(day.value))}
                                      onChange={selectedDays =>
                                        updateSchedule(
                                          scheduleIndex,
                                          'days',
                                          selectedDays ? selectedDays.map(d => d.value) : []
                                        )
                                      }
                                      placeholder="Select days"
                                      styles={selectStyles}
                                      theme={theme => ({
                                        ...theme,
                                        borderRadius: 5,
                                        colors: { ...theme.colors, primary25: '#253746', primary: '#bd472a' }
                                      })}
                                    />
                                  </div>
                                  <div className="mb-2">
                                    <small className="text-muted">Times (UTC):</small>
                                    {schedule.times.map((time, timeIndex) => (
                                      <div key={timeIndex} className="d-flex align-items-center mb-1">
                                        <input
                                          type="time"
                                          value={time}
                                          onChange={e => updateTimeInSchedule(scheduleIndex, timeIndex, e.target.value)}
                                          className="form-control form-control-sm me-2"
                                          style={{ width: '120px' }}
                                        />
                                        {schedule.times.length > 1 && (
                                          <Button
                                            color="falcon-danger"
                                            size="sm"
                                            onClick={() => removeTimeFromSchedule(scheduleIndex, timeIndex)}
                                          >
                                            <FontAwesomeIcon icon="times" />
                                          </Button>
                                        )}
                                        {timeIndex === schedule.times.length - 1 && (
                                          <Button
                                            color="falcon-default"
                                            size="sm"
                                            onClick={() => addTimeToSchedule(scheduleIndex)}
                                            className="ms-2"
                                          >
                                            <FontAwesomeIcon icon="plus" />
                                          </Button>
                                        )}
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              ))}

                              {schedules.length > 0 && (
                                <Button color="falcon-default" size="sm" onClick={addNewSchedule}>
                                  <FontAwesomeIcon icon="plus" className="me-1" /> Add Another Schedule
                                </Button>
                              )}
                            </div>
                          )}

                          {/* Action Buttons */}
                          <div className="d-flex justify-content-between mt-4">
                            <Button
                              color="falcon-default"
                              onClick={() => saveScheduleOnly()}
                              disabled={!validateSchedulingConfiguration()}
                            >
                              Save
                            </Button>
                            <Button
                              color="falcon-default"
                              onClick={() => forceRun()}
                              disabled={!isConfigured || !validateSchedulingConfiguration()}
                            >
                              Save &amp; Run Now
                            </Button>
                          </div>
                        </CardBody>
                      </Card>
                    )}
                  </div>

                  {/* Right Card: Vendor API Status + Integration Guide */}
                  <div style={{ flex: 1 }}>
                    <Card className="h-100">
                      <CardBody>
                        {/* Vendor API Status */}
                        <div className="mb-4">
                          <Row className="align-items-center mb-2">
                            <Col>
                              <h6 className="mb-0" style={{ fontWeight: 600 }}>
                                Vendor API status
                              </h6>
                            </Col>
                            <Col xs="auto">
                              <div className="d-flex align-items-center">
                                <span className="fa-stack fa-xs me-2">
                                  <FontAwesomeIcon
                                    icon="circle"
                                    className={`branding-${
                                      vendorApiStatus?.currentStatus?.status === 'OK'
                                        ? 'green'
                                        : vendorApiStatus?.currentStatus?.status === 'DEGRADED'
                                        ? 'yellow'
                                        : ['UNAVAILABLE', 'ERROR'].includes(vendorApiStatus?.currentStatus?.status)
                                        ? 'red'
                                        : 'grey'
                                    } fa-stack-2x`}
                                  />
                                  <FontAwesomeIcon
                                    icon={
                                      vendorApiStatus?.currentStatus?.status === 'OK'
                                        ? 'check'
                                        : vendorApiStatus?.currentStatus?.status === 'DEGRADED'
                                        ? 'exclamation'
                                        : ['UNAVAILABLE', 'ERROR'].includes(vendorApiStatus?.currentStatus?.status)
                                        ? 'times'
                                        : 'question'
                                    }
                                    className="fa-stack-1x"
                                    inverse
                                  />
                                </span>
                                <span
                                  className={`branding-${
                                    vendorApiStatus?.currentStatus?.status === 'OK'
                                      ? 'green'
                                      : vendorApiStatus?.currentStatus?.status === 'DEGRADED'
                                      ? 'yellow'
                                      : ['UNAVAILABLE', 'ERROR'].includes(vendorApiStatus?.currentStatus?.status)
                                      ? 'red'
                                      : 'grey'
                                  } fw-semibold`}
                                  style={{ fontSize: '0.875rem' }}
                                >
                                  {vendorApiStatus?.currentStatus?.status === 'OK'
                                    ? 'OPERATIONAL'
                                    : vendorApiStatus?.currentStatus?.status || 'UNKNOWN'}
                                </span>
                              </div>
                            </Col>
                          </Row>
                          <p className="text-muted small mb-0">
                            Last checked: {lastChecked ? moment(lastChecked).format('MMM D, YYYY h:mm A') : 'Unknown'}
                          </p>
                        </div>

                        {/* Managed Products Count */}
                        <div className="mb-4">
                          <Row className="align-items-center">
                            <Col>
                              <h6 className="mb-0" style={{ fontWeight: 600 }}>
                                Managed products
                              </h6>
                            </Col>
                            <Col xs="auto">
                              <span className="fw-semibold" style={{ fontSize: '0.875rem' }}>
                                {managedProductCount}
                              </span>
                            </Col>
                          </Row>
                        </div>

                        {/* Vendor Integration Guide */}
                        <div>
                          <h6 className="mb-2" style={{ fontWeight: 600 }}>
                            Vendor Integration Guide
                          </h6>
                          <a
                            href={getIntegrationGuideUrl()}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-primary"
                            style={{ textDecoration: 'none' }}
                          >
                            Open {currentVendor?.name?.replace('Systems', 'Networks')} Integration Guide
                          </a>
                        </div>
                      </CardBody>
                    </Card>
                  </div>
                </div>

                {/* Section 3: Bug Chart */}
                <Card style={{ marginBottom: '5px', backgroundColor: 'var(--bg-soft)' }}>
                  <CardBody>
                    <Row className="align-items-center mb-4">
                      <Col xs="auto">
                        <Select
                          options={bugOptions}
                          value={selectedBugOption}
                          onChange={handleBugOptionChange}
                          styles={selectStyles}
                          theme={theme => ({
                            ...theme,
                            borderRadius: 5,
                            colors: { ...theme.colors, primary25: '#253746', primary: '#bd472a' }
                          })}
                          className="shadow-sm"
                        />
                      </Col>
                      <Col className="text-end">
                        <LoaderButton color="falcon-default" onClick={updateServices} isLoading={isLoading}>
                          Refresh
                        </LoaderButton>
                      </Col>
                    </Row>

                    <Row className="align-items-center">
                      <Col md={6} className="d-flex justify-content-center">
                        <div className="position-relative" style={{ width: '250px', height: '250px' }}>
                          <ReactEcharts
                            echarts={echarts}
                            option={getChartOption()}
                            style={{ width: '100%', height: '100%' }}
                          />
                          <div
                            className="position-absolute text-center"
                            style={{
                              top: '50%',
                              left: '50%',
                              transform: 'translate(-50%, -50%)',
                              fontSize: '2.5rem',
                              fontWeight: 600,
                              color: 'var(--ink)'
                            }}
                          >
                            {numberFormatter(totalBugs, 0)}
                          </div>
                        </div>
                      </Col>
                      <Col md={6}>
                        <div className="ps-4">
                          {priorityStats.length > 0 ? (
                            priorityStats.map((item, index) => (
                              <div key={index} className="d-flex align-items-center mb-2">
                                <div
                                  style={{
                                    width: '16px',
                                    height: '16px',
                                    backgroundColor: getSeverityColor(item.name),
                                    borderRadius: '3px',
                                    marginRight: '10px'
                                  }}
                                />
                                <span>
                                  {item.name} ({item.value})
                                </span>
                              </div>
                            ))
                          ) : (
                            <p className="text-muted">No bugs found</p>
                          )}
                        </div>
                      </Col>
                    </Row>
                  </CardBody>
                </Card>
              </>
            )}
          </>
        )}
      </TabPane>
    </div>
  );
}

TabOne.propTypes = {
  vendorId: PropTypes.string,
  vendorLabel: PropTypes.string,
  activeTab: PropTypes.string,
  setActiveTab: PropTypes.func,
  snIntegrationCheck: PropTypes.bool,
  isConfigured: PropTypes.bool,
  modalOpen: PropTypes.bool,
  setModalOpen: PropTypes.func,
  setIsForceRun: PropTypes.func,
  timeUnit: PropTypes.string,
  setTimeUnit: PropTypes.func,
  timeValue: PropTypes.object,
  setTimeValue: PropTypes.func,
  schedulingMode: PropTypes.string,
  setSchedulingMode: PropTypes.func,
  schedules: PropTypes.array,
  setSchedules: PropTypes.func,
  loading: PropTypes.bool,
  setIsServiceActive: PropTypes.func,
  isServiceActive: PropTypes.bool,
  execStatus: PropTypes.shape({
    hasExecution: PropTypes.bool,
    configured: PropTypes.bool,
    notReadyReason: PropTypes.string,
    isRunning: PropTypes.bool,
    vendorId: PropTypes.string,
    executionArn: PropTypes.string,
    executionStatus: PropTypes.string,
    stateName: PropTypes.string,
    displayName: PropTypes.string,
    stateType: PropTypes.string,
    startedAt: PropTypes.string,
    enteredAt: PropTypes.string,
    stoppedAt: PropTypes.string,
    caughtError: PropTypes.shape({
      state: PropTypes.string,
      displayName: PropTypes.string,
      exceptionName: PropTypes.string,
      errorCode: PropTypes.string,
      policy: PropTypes.string,
      message: PropTypes.string,
      outcome: PropTypes.oneOf(['FAILED', 'NOT_CONFIGURED', 'STOPPED'])
    }),
    progress: PropTypes.shape({
      total: PropTypes.number,
      completed: PropTypes.number,
      failed: PropTypes.number,
      running: PropTypes.number
    })
  }),
  execStatusLoading: PropTypes.bool,
  refreshExecStatus: PropTypes.func,
  onEditSchedule: PropTypes.func
};
